import React, {ReactNode, createContext, useCallback, useContext, useMemo, useState} from 'react'
import {defineStyles, useThemedStyles} from './styles/style'

import {StyleValues} from './styles/types'
import {modalBorderStyle} from './shared'
import {useOnClickOutside} from './hooks'

interface TooltipContextType {
  isOpen: boolean
  toggleTooltip: () => void
  closeTooltip: () => void
}

const defaultContext: TooltipContextType = {isOpen: false, toggleTooltip: () => {}, closeTooltip: () => {}}

export const TooltipContext = createContext(defaultContext)

type ToolTipChildProps = {children: ReactNode}

export const TooltipProvider: React.FC<ToolTipChildProps> = ({children}) => {
  const [isOpen, setIsOpen] = useState(false)
  const s = useThemedStyles(tooltipStyles)

  const toggleTooltip = useCallback(() => {
    setIsOpen((curr) => !curr)
  }, [])

  const closeTooltip = useCallback(() => {
    setIsOpen(false)
  }, [])

  const tooltipRef = useOnClickOutside(closeTooltip)

  const values = useMemo(() => ({isOpen, toggleTooltip, closeTooltip}), [isOpen, toggleTooltip, closeTooltip])
  return (
    <TooltipContext.Provider value={values}>
      <div style={s.container} ref={tooltipRef}>
        {children}
      </div>
    </TooltipContext.Provider>
  )
}

export const TooltipTrigger: React.FC<ToolTipChildProps> = ({children}) => {
  const {toggleTooltip} = useContext(TooltipContext)
  const s = useThemedStyles(tooltipStyles)

  return (
    <div onClick={toggleTooltip} style={s.triggerContainer}>
      {children}
    </div>
  )
}

interface ToolTipContentProps {
  variant: 'bottom' | 'right'
}

export const TooltipContent: React.FC<ToolTipContentProps & ToolTipChildProps> = ({children, variant = 'right'}) => {
  const {isOpen} = useContext(TooltipContext)
  const s = useThemedStyles(tooltipStyles)

  const contentStyle = useMemo(
    () => (variant === 'bottom' ? s.tooltipBottom : s.tooltipLeft),
    [s.tooltipBottom, s.tooltipLeft, variant]
  )

  if (!isOpen) return null

  return isOpen && <div style={contentStyle}>{children}</div>
}

const tooltipStyles = defineStyles((t, font) => {
  const contentPadding = 12
  const content: StyleValues = {
    ...modalBorderStyle(t),
    ...font.bodyMedium,
    backgroundColor: t.core.b1,
    padding: `${contentPadding}px`,
    color: t.core.f2,
    position: 'absolute',
    width: '240px',
    overflowWrap: 'break-word'
  }

  return {
    container: {
      position: 'relative',
      zIndex: 12
    },

    tooltipBottom: {
      ...content,
      transform: `translate(calc(-50% + ${contentPadding / 2}px), 12px)`
    },

    tooltipLeft: {
      ...content,
      transform: `translate(30px, calc(-50% - 12px))`
    },

    triggerContainer: {
      cursor: 'pointer'
    }
  }
})
